"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import type { LaptopInformation, LaptopScores } from "@/lib/types";
import { toSlug } from "@/lib/utils";
import { type Answer, type Reference, buildComparisonSet, capitalize, computeCloseness, genBatteryText, genBudgetText, genFormFactorText, genFutureProofText, genPerformanceText, genRamText, genStorageText, genValueText, scoreBand, scoreColor } from "@/lib/match-explanations";
export type ScoreDetailModalProps = {
  item: LaptopInformation;
  /**
   * Passed in rather than read off `item`, because a /spec/ laptop payload
   * carries `scores: null` — its scores are fetched separately after the Q&A.
   */
  scores: LaptopScores;
  rank: number;
  allItems: LaptopInformation[];
  reference: Reference;
  answers: Record<string, Answer>;
  country: string;
  context: "recommendation" | "laptop";
  onClose: () => void;
};
function barWidth(score: number): string {
  return `${Math.max(0, Math.min(100, score))}%`;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconClose() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-4"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconZap() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-3 shrink-0 text-muted-foreground"><path d="M9.5 1.5 3 9h5.5L6.5 14.5 13 7H7.5L9.5 1.5Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconDisplay() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><rect x="1" y="2" width="14" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.25" /><path d="M5 13h6M8 11v2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconCpu() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><rect x="4" y="4" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.25" /><path d="M6 2v2M10 2v2M6 12v2M10 12v2M2 6h2M2 10h2M12 6h2M12 10h2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconLeaf() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><path d="M2.5 13.5C3.5 8.5 6 5 13.5 2.5 11 9.5 8.5 12 2.5 13.5Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" /><path d="M2.5 13.5L6 10" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconShield() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><path d="M8 1.5 2.5 4v4c0 3 2.5 5.5 5.5 6.5 3-1 5.5-3.5 5.5-6.5V4L8 1.5Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconTag() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><path d="M2 2h5l7 7-5 5-7-7V2Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" /><circle cx="5.5" cy="5.5" r="1" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconRam() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><rect x="1" y="5" width="14" height="6" rx="1" stroke="currentColor" strokeWidth="1.25" /><path d="M4 5V3M7 5V3M10 5V3M4 11v2M7 11v2M10 11v2" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" /><circle cx="4" cy="8" r="0.6" fill="currentColor" /><circle cx="7" cy="8" r="0.6" fill="currentColor" /><circle cx="10" cy="8" r="0.6" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconStorage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="none" className="size-3 shrink-0"><rect x="1.5" y="2.5" width="13" height="4" rx="1" stroke="currentColor" strokeWidth="1.25" /><rect x="1.5" y="9.5" width="13" height="4" rx="1" stroke="currentColor" strokeWidth="1.25" /><circle cx="12.5" cy="4.5" r="0.7" fill="currentColor" /><circle cx="12.5" cy="11.5" r="0.7" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconCheck() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-2.5 text-brand"><path fillRule="evenodd" d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7 7a.75.75 0 0 1-1.06 0l-3.5-3.5a.75.75 0 1 1 1.06-1.06L6.25 10.69l6.47-6.47a.75.75 0 0 1 1.06 0Z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function IconCross() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg viewBox="0 0 16 16" fill="currentColor" className="size-2.5"><path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ── Sub-components ─────────────────────────────────────────────────────────────

// `label` overrides the Yes/No text where a bare "Yes" would be meaningless —
// a laptop can't match a screen size the user never expressed a view on.
function BoolBadge(t0) {
  const $ = _c(7);
  const {
    value,
    label
  } = t0;
  const neutral = label != null;
  const t1 = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${value && !neutral ? "bg-brand/10 text-brand ring-1 ring-brand/20" : "bg-foreground/5 text-muted-foreground ring-1 ring-border"}`;
  let t2;
  if ($[0] !== neutral || $[1] !== value) {
    t2 = !neutral && (value ? <IconCheck /> : <IconCross />);
    $[0] = neutral;
    $[1] = value;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = label ?? (value ? "Yes" : "No");
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = <span className={t1}>{t2}{t3}</span>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function WhyButton(t0) {
  const $ = _c(10);
  const {
    isOpen,
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = e => {
      e.stopPropagation();
      onClick();
    };
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `grid size-4 place-items-center rounded-full text-[9px] font-bold transition-colors ${isOpen ? "bg-foreground text-background" : "bg-foreground/8 text-muted-foreground hover:bg-foreground/15"}`;
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <button onClick={t1} className={t2} aria-label="See why">?</button>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isOpen) {
    t4 = !isOpen && <div className="pointer-events-none absolute bottom-full right-0 z-10 mb-2 w-28 rounded-lg bg-foreground px-2 py-1.5 text-center text-[10px] leading-snug text-background opacity-0 shadow-lg transition-opacity group-hover:opacity-100">click to see why<div className="absolute right-2 top-full border-[4px] border-transparent border-t-foreground" /></div>;
    $[5] = isOpen;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="group relative shrink-0">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function AccordionText(t0) {
  const $ = _c(2);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    t1 = <div className="mt-2 rounded-xl bg-surface px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground ring-1 ring-border/40">{text}</div>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// ── Modal ─────────────────────────────────────────────────────────────────────

export function ScoreDetailModal(t0) {
  const $ = _c(114);
  const {
    item,
    scores,
    allItems: allItemsProp,
    reference,
    answers,
    country,
    context,
    onClose
  } = t0;
  const router = useRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [openRows, setOpenRows] = useState(t1);
  const matchScore = scores.overall;
  const allItems = buildComparisonSet(item, scores, allItemsProp);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = key => {
      setOpenRows(prev => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggleRow = t2;
  let t3;
  let t4;
  if ($[2] !== onClose) {
    t3 = () => {
      const handler = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    };
    t4 = [onClose];
    $[2] = onClose;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useEffect(_temp2, t5);
  const refRam = reference.ram_size_gigabytes as number | null;
  const refStorage = reference.storage_size_gigabytes as number | null;
  const ramCloseness = refRam ? computeCloseness(item.specs.ram.size_gb, refRam) : null;
  const storageCloseness = refStorage ? computeCloseness(item.specs.storage.size_gb, refStorage) : null;
  const screenAnswer = answers.screen as string | undefined;
  const screenAsked = Boolean(screenAnswer) && screenAnswer !== "none";
  let t6;
  if ($[6] !== country || $[7] !== item.laptop.config_number || $[8] !== item.laptop.series || $[9] !== onClose || $[10] !== router) {
    t6 = () => {
      router.push(`/${country}/${toSlug(item.laptop.series)}/${item.laptop.config_number}`);
      onClose();
    };
    $[6] = country;
    $[7] = item.laptop.config_number;
    $[8] = item.laptop.series;
    $[9] = onClose;
    $[10] = router;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleSeeMoreDetails = t6;
  const t7 = createPortal;
  const t8 = "fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center";
  const t9 = "dialog";
  const t10 = "true";
  const t11 = `Score breakdown for ${item.laptop.name}`;
  let t12;
  if ($[12] !== onClose) {
    t12 = <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />;
    $[12] = onClose;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const t13 = "relative z-10 flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-card ring-1 ring-border/60 shadow-2xl";
  let t14;
  if ($[14] !== item.laptop.company) {
    t14 = capitalize(item.laptop.company);
    $[14] = item.laptop.company;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t14) {
    t15 = <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-brand">{t14}</p>;
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== item.laptop.config_number || $[19] !== item.laptop.series) {
    t16 = <h3 className="mt-0.5 text-base font-bold leading-snug tracking-tight">{item.laptop.series} Model {item.laptop.config_number}</h3>;
    $[18] = item.laptop.config_number;
    $[19] = item.laptop.series;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== t15 || $[22] !== t16) {
    t17 = <div className="flex-1 min-w-0">{t15}{t16}</div>;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <IconClose />;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== onClose) {
    t19 = <button onClick={onClose} aria-label="Close" className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-surface text-muted-foreground transition-colors hover:bg-foreground hover:text-background">{t18}</button>;
    $[25] = onClose;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t17 || $[28] !== t19) {
    t20 = <div className="flex items-start gap-3 border-b border-border/60 px-5 py-4">{t17}{t19}</div>;
    $[27] = t17;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  const t21 = "flex-1 overflow-y-auto px-5 py-5";
  let t22;
  if ($[30] !== matchScore) {
    t22 = scoreColor(matchScore);
    $[30] = matchScore;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== t22) {
    t23 = {
      backgroundColor: t22
    };
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== matchScore) {
    t24 = <span className="text-2xl font-bold leading-none tabular-nums">{matchScore}</span>;
    $[34] = matchScore;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== t23 || $[37] !== t24) {
    t25 = <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl text-white" style={t23}>{t24}</div>;
    $[36] = t23;
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <p className="text-sm font-bold leading-none text-foreground">Match</p>;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== matchScore) {
    t27 = scoreBand(matchScore);
    $[40] = matchScore;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== t27) {
    t28 = <div>{t26}<p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t27}</p></div>;
    $[42] = t27;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== t25 || $[45] !== t28) {
    t29 = <div className="flex items-center gap-3">{t25}{t28}</div>;
    $[44] = t25;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <p className="mt-2 text-xs font-medium leading-snug text-muted-foreground">How well this laptop matches your specific needs</p>;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  const t31 = "mt-4 space-y-3 border-t border-border/50 pt-4";
  let t32;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconZap />Within budget</span>;
    $[48] = t32;
  } else {
    t32 = $[48];
  }
  let t33;
  if ($[49] !== scores.within_budget) {
    t33 = <BoolBadge value={scores.within_budget} />;
    $[49] = scores.within_budget;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== openRows) {
    t34 = openRows.has("budget");
    $[51] = openRows;
    $[52] = t34;
  } else {
    t34 = $[52];
  }
  let t35;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => toggleRow("budget");
    $[53] = t35;
  } else {
    t35 = $[53];
  }
  let t36;
  if ($[54] !== t34) {
    t36 = <WhyButton isOpen={t34} onClick={t35} />;
    $[54] = t34;
    $[55] = t36;
  } else {
    t36 = $[55];
  }
  let t37;
  if ($[56] !== t33 || $[57] !== t36) {
    t37 = <div className="flex items-center justify-between gap-2">{t32}<div className="flex items-center gap-1.5">{t33}{t36}</div></div>;
    $[56] = t33;
    $[57] = t36;
    $[58] = t37;
  } else {
    t37 = $[58];
  }
  let t38;
  if ($[59] !== answers || $[60] !== item || $[61] !== openRows || $[62] !== scores.within_budget) {
    t38 = openRows.has("budget") && <AccordionText text={genBudgetText(scores.within_budget, item, answers)} />;
    $[59] = answers;
    $[60] = item;
    $[61] = openRows;
    $[62] = scores.within_budget;
    $[63] = t38;
  } else {
    t38 = $[63];
  }
  let t39;
  if ($[64] !== t37 || $[65] !== t38) {
    t39 = <div>{t37}{t38}</div>;
    $[64] = t37;
    $[65] = t38;
    $[66] = t39;
  } else {
    t39 = $[66];
  }
  let t40;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconDisplay />Preferred size</span>;
    $[67] = t40;
  } else {
    t40 = $[67];
  }
  const t41 = screenAsked ? undefined : "Not specified";
  let t42;
  if ($[68] !== scores.form_factor || $[69] !== t41) {
    t42 = <BoolBadge value={scores.form_factor} label={t41} />;
    $[68] = scores.form_factor;
    $[69] = t41;
    $[70] = t42;
  } else {
    t42 = $[70];
  }
  let t43;
  if ($[71] !== openRows) {
    t43 = openRows.has("size");
    $[71] = openRows;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = () => toggleRow("size");
    $[73] = t44;
  } else {
    t44 = $[73];
  }
  let t45;
  if ($[74] !== t43) {
    t45 = <WhyButton isOpen={t43} onClick={t44} />;
    $[74] = t43;
    $[75] = t45;
  } else {
    t45 = $[75];
  }
  let t46;
  if ($[76] !== t42 || $[77] !== t45) {
    t46 = <div className="flex items-center justify-between gap-2">{t40}<div className="flex items-center gap-1.5">{t42}{t45}</div></div>;
    $[76] = t42;
    $[77] = t45;
    $[78] = t46;
  } else {
    t46 = $[78];
  }
  let t47;
  if ($[79] !== answers || $[80] !== item || $[81] !== openRows || $[82] !== scores.form_factor) {
    t47 = openRows.has("size") && <AccordionText text={genFormFactorText(scores.form_factor, item, answers)} />;
    $[79] = answers;
    $[80] = item;
    $[81] = openRows;
    $[82] = scores.form_factor;
    $[83] = t47;
  } else {
    t47 = $[83];
  }
  let t48;
  if ($[84] !== t46 || $[85] !== t47) {
    t48 = <div>{t46}{t47}</div>;
    $[84] = t46;
    $[85] = t47;
    $[86] = t48;
  } else {
    t48 = $[86];
  }
  const t49 = scores.performance !== null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconCpu />Performance</span><div className="flex items-center gap-1.5"><span className="text-sm font-bold tabular-nums" style={{
          color: scoreColor(scores.performance)
        }}>{scores.performance}%</span><WhyButton isOpen={openRows.has("perf")} onClick={() => toggleRow("perf")} /></div></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
        width: barWidth(scores.performance),
        backgroundColor: scoreColor(scores.performance)
      }} /></div>{openRows.has("perf") && <AccordionText text={genPerformanceText(scores.performance, item, allItems, reference, answers)} />}</div>;
  const t50 = scores.battery_life !== null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconLeaf />{"Battery & efficiency"}</span><div className="flex items-center gap-1.5"><span className="text-sm font-bold tabular-nums" style={{
          color: scoreColor(scores.battery_life)
        }}>{scores.battery_life}%</span><WhyButton isOpen={openRows.has("battery")} onClick={() => toggleRow("battery")} /></div></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
        width: barWidth(scores.battery_life),
        backgroundColor: scoreColor(scores.battery_life)
      }} /></div>{openRows.has("battery") && <AccordionText text={genBatteryText(scores.battery_life, item, allItems, answers)} />}</div>;
  const t51 = scores.future_proof != null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconShield />Future-proofing</span><div className="flex items-center gap-1.5"><span className="text-sm font-bold tabular-nums" style={{
          color: scoreColor(scores.future_proof)
        }}>{scores.future_proof}%</span><WhyButton isOpen={openRows.has("future")} onClick={() => toggleRow("future")} /></div></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
        width: barWidth(scores.future_proof),
        backgroundColor: scoreColor(scores.future_proof)
      }} /></div>{openRows.has("future") && <AccordionText text={genFutureProofText(scores.future_proof, item, allItems)} />}</div>;
  const t52 = scores.value_for_money != null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconTag />Value for money</span><div className="flex items-center gap-1.5"><span className="text-sm font-bold tabular-nums" style={{
          color: scoreColor(scores.value_for_money)
        }}>{scores.value_for_money}%</span><WhyButton isOpen={openRows.has("value")} onClick={() => toggleRow("value")} /></div></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
        width: barWidth(scores.value_for_money),
        backgroundColor: scoreColor(scores.value_for_money)
      }} /></div>{openRows.has("value") && <AccordionText text={genValueText(scores.value_for_money, item, allItems, answers)} />}</div>;
  const t53 = ramCloseness !== null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconRam />RAM</span><div className="flex items-center gap-1.5"><span className="text-sm font-bold tabular-nums" style={{
          color: scoreColor(ramCloseness)
        }}>{ramCloseness}%</span><WhyButton isOpen={openRows.has("ram")} onClick={() => toggleRow("ram")} /></div></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
        width: barWidth(ramCloseness),
        backgroundColor: scoreColor(ramCloseness)
      }} /></div>{openRows.has("ram") && <AccordionText text={genRamText(ramCloseness, item, allItems, reference, answers)} />}</div>;
  const t54 = storageCloseness !== null && <div><div className="flex items-center justify-between gap-1"><span className="flex items-center gap-1.5 text-[11px] font-medium text-foreground"><IconStorage />Storage</span><div className="flex items-center gap-1.5"><span className="text-sm font-bold tabular-nums" style={{
          color: scoreColor(storageCloseness)
        }}>{storageCloseness}%</span><WhyButton isOpen={openRows.has("storage")} onClick={() => toggleRow("storage")} /></div></div><div className="mt-1.5 h-1 overflow-hidden rounded-full bg-border/60"><div className="h-full rounded-full transition-all duration-500" style={{
        width: barWidth(storageCloseness),
        backgroundColor: scoreColor(storageCloseness)
      }} /></div>{openRows.has("storage") && <AccordionText text={genStorageText(storageCloseness, item, allItems, reference, answers)} />}</div>;
  let t55;
  if ($[87] !== t39 || $[88] !== t48 || $[89] !== t49 || $[90] !== t50 || $[91] !== t51 || $[92] !== t52 || $[93] !== t53 || $[94] !== t54) {
    t55 = <div className={t31}>{t39}{t48}{t49}{t50}{t51}{t52}{t53}{t54}</div>;
    $[87] = t39;
    $[88] = t48;
    $[89] = t49;
    $[90] = t50;
    $[91] = t51;
    $[92] = t52;
    $[93] = t53;
    $[94] = t54;
    $[95] = t55;
  } else {
    t55 = $[95];
  }
  let t56;
  if ($[96] !== t29 || $[97] !== t30 || $[98] !== t55) {
    t56 = <div className={t21}>{t29}{t30}{t55}</div>;
    $[96] = t29;
    $[97] = t30;
    $[98] = t55;
    $[99] = t56;
  } else {
    t56 = $[99];
  }
  let t57;
  if ($[100] !== context || $[101] !== handleSeeMoreDetails) {
    t57 = context === "recommendation" && <div className="border-t border-border/60 px-5 py-4"><button onClick={handleSeeMoreDetails} className="flex h-11 w-full items-center justify-center rounded-full bg-brand px-4 text-sm font-semibold text-brand-foreground shadow-[0_4px_18px_-4px_oklch(0.71_0.14_165_/_0.55)] transition-all hover:opacity-90 active:scale-[0.98]">See why it fits for you →</button></div>;
    $[100] = context;
    $[101] = handleSeeMoreDetails;
    $[102] = t57;
  } else {
    t57 = $[102];
  }
  let t58;
  if ($[103] !== t20 || $[104] !== t56 || $[105] !== t57) {
    t58 = <div className={t13}>{t20}{t56}{t57}</div>;
    $[103] = t20;
    $[104] = t56;
    $[105] = t57;
    $[106] = t58;
  } else {
    t58 = $[106];
  }
  let t59;
  if ($[107] !== t11 || $[108] !== t12 || $[109] !== t58) {
    t59 = <div className={t8} role={t9} aria-modal={t10} aria-label={t11}>{t12}{t58}</div>;
    $[107] = t11;
    $[108] = t12;
    $[109] = t58;
    $[110] = t59;
  } else {
    t59 = $[110];
  }
  let t60;
  if ($[111] !== t59 || $[112] !== t7) {
    t60 = t7(t59, document.body);
    $[111] = t59;
    $[112] = t7;
    $[113] = t60;
  } else {
    t60 = $[113];
  }
  return t60;
}
function _temp2() {
  document.body.style.overflow = "hidden";
  return _temp;
}
function _temp() {
  document.body.style.overflow = "";
}